import { CssBaseline, ThemeProvider, Box, CircularProgress } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AppRouter } from './router/AppRouter';
import { adminTheme } from './theme/adminTheme';
import { AuthProvider } from './providers/AuthProvider';
import { useAuth } from './hooks/useAuth';
import { UserScopeProvider } from './providers/UserScopeProvider';
import { LoginPage } from './pages/Login/LoginPage';
import { ApiError } from './services/apiClient';
import { AuthErrorBoundary } from './components/AuthErrorBoundary';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on auth errors
        if (error instanceof ApiError && error.isAuthError) {
          return false;
        }
        // Retry other errors up to 1 time
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: (_failureCount, error) => {
        // Don't retry on auth errors
        if (error instanceof ApiError && error.isAuthError) {
          return false;
        }
        return false; // Don't retry mutations by default
      },
    },
  },
});

const AppContent: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
};

function App() {
  return (
    <AuthErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={adminTheme}>
          <CssBaseline />
          <AuthProvider>
            <UserScopeProvider>
              <AppContent />
            </UserScopeProvider>
          </AuthProvider>
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </ThemeProvider>
      </QueryClientProvider>
    </AuthErrorBoundary>
  );
}

export default App;
